// this module replaces our previous dependency on the querystring library

/**
 * Converts a URL's query string to an object of string keys to string values.
 * Note that this automatically removes question marks from the query string.
 * @example 
 * ```
 * const result = parse("a=1&b=2");
 * // result is {a: "1", b: "2"}
 * ```
 * @param {string} queryString the URL query string to convert.
 * @returns {object} the converted query string
 */
export function parse(queryString) {
    /*
        cannot simply return 'new URLSearchParams(queryString)',
        as the program expects a basic object, not an instance of URLSearchParams
    */
    const complexObj = new URLSearchParams(queryString);
    const uniqueKeys = new Set(complexObj.keys()); // keys() returns duplicates; do not want
    const entries = Array.from(uniqueKeys)
        .map(k => {
            // only return an array if multiple values exist for a key
            const allValues = complexObj.getAll(k);
            if (allValues.length === 1) {
                return [k, allValues[0]];
            } else {
                return [k, allValues];
            }
        });
    const result = Object.fromEntries(entries);
    return result;
}

/**
 * Converts an object of key-value pairs to a query string containing those pairs.
 * @example
 * ```
 * const result = stringify({foo: 1, bar: 2});
 * // result is "foo=1&bar=2"
 * ```
 * @param {*} obj an object whose properties should be joined into a query string
 * @returns the query string
 */
export function stringify(obj) {
    /*
        cannot simply use 'new URLSearchParams(obj).toString()', 
        as it serializes array values in obj differently than how the program expects

        const result = sut.stringify({foo: 1, bar: [2, 3]});
        expect(result).toBe("foo=1&bar=2&bar=3");
        Expected: "foo=1&bar=2&bar=3"
        Received: "foo=1&bar=2%2C3"
    */

    // https://stackoverflow.com/a/63660306
    const result = new URLSearchParams();
    Object.entries(obj)
        .forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach(item => result.append(key, _formatValue(item)));
            } else {
                result.append(key, _formatValue(value));
            }
        });
    
    if (result == null){
        result = "";
    }
    return result.toString();
}

function _formatValue(value) {
    if (value == null){
        value = "";
    }
    if (typeof(value) === "object") {
        return ""; // the querystring library this replaces explicitely converts objects to empty strings
    }
    return value.toString();
}

export default {
    parse: parse,
    stringify: stringify
}